import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Image,
  Grid,
  Box,
  List,
  ThemeIcon,
  Center,
  Flex
} from '@mantine/core';
import {
  IconBrain,
  IconCalendarWeek,
  IconCheck,
  IconCoin,
  IconListCheck,
  IconMoodSad,
  IconPackage,
  IconSearch,
  IconShoppingCart
} from '@tabler/icons-react';
import { AuthButton } from '../AuthButton';
import { GradientText } from '../UI/GradientText';
import { HeroRecipeShowcase } from './HeroRecipeShowcase';
import { heroFontFamily } from '../../theme';
import { SocialProof } from './SocialProof';
import { usePageTitle } from '../../hooks/usePageTitle';

export const CampaignLandingPage = () => {
  usePageTitle('Meal Plans for Real Dietary Needs | Meals You Love');
  return (
    <Box>
      {/* ──────────────────────────────────────────────
          SECTION 1: HERO
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Grid align="flex-start" gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xl">
              <Title
                order={1}
                c="dark"
                style={{ fontFamily: heroFontFamily }}
              >
                Meal plans built for{' '}
                <Box component="br" hiddenFrom="md" style={{ display: 'none' }} />
                <Box component="br" visibleFrom="md" />
                <GradientText>real dietary needs</GradientText>
              </Title>

              <Text size="lg" c="dimmed" lh={1.6}>
                Gluten-free and low-sodium? Dairy-free and diabetic-friendly? Combine
                as many restrictions as you need, then shop through Kroger, Instacart,
                or your local store.
              </Text>

              <Stack gap="sm">
                <Flex gap="sm" align="flex-start">
                  <IconCheck size={15} stroke={2.4} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 4 }} />
                  <Text c="gray.8">Plans that actually understand complex dietary needs</Text>
                </Flex>
                <Flex gap="sm" align="flex-start">
                  <IconCheck size={15} stroke={2.4} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 4 }} />
                  <Text c="gray.8">Your whole week of dinners, planned in seconds</Text>
                </Flex>
                <Flex gap="sm" align="flex-start">
                  <IconCheck size={15} stroke={2.4} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 4 }} />
                  <Text c="gray.8">$9.99/month vs. $250-500+/month for meal kits</Text>
                </Flex>
              </Stack>

              <Text size="sm" c="dimmed" fs="italic">
                "I have dietary limits that were next to impossible to work in until now." — Sheila S.
              </Text>

              {/* Mobile CTA */}
              <Group gap="md" style={{ flexDirection: 'column' }} hiddenFrom="sm">
                {/* Fluid font keeps the long label from clipping at ~320px */}
                <AuthButton size="lg" fullWidth styles={{ root: { fontSize: 'clamp(0.9375rem, 4.5vw, 1.125rem)' } }}>
                  Build your personalized plan
                </AuthButton>
              </Group>
              {/* Tablet CTA */}
              <Group gap="md" visibleFrom="sm" hiddenFrom="md">
                <AuthButton size="md">
                  Build your personalized plan
                </AuthButton>
              </Group>
              {/* Desktop CTA */}
              <Group gap="md" visibleFrom="md">
                <AuthButton size="lg">
                  Build your personalized plan
                </AuthButton>
              </Group>

              <Group gap="xs" justify="center" hiddenFrom="sm">
                <Text size="sm" c="dimmed">
                  1-month free trial • No credit card required
                </Text>
              </Group>
              <Group gap="xs" visibleFrom="sm">
                <Text size="sm" c="dimmed">
                  1-month free trial • No credit card required
                </Text>
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Center>
              <HeroRecipeShowcase
                onCtaClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </Center>
          </Grid.Col>
        </Grid>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 2: THE PROBLEM
      ────────────────────────────────────────────── */}
      <Box
        py={{ base: 40, md: 80 }}
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-gray-0) 0%, rgba(255, 250, 245, 1) 100%)'
        }}
      >
        <Container size="lg">
          <Stack gap="xl">
            <Center>
              <Stack gap="sm" align="center" maw={650}>
                <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                  Managing a health condition is hard enough
                </Title>
                <Text size="lg" ta="center" c="dimmed">
                  Meal planning shouldn't make it harder.
                </Text>
              </Stack>
            </Center>

            <Grid align="stretch" gutter="xl">
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Paper p="xl" radius="lg" h="100%" style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid var(--mantine-color-orange-2)'
                }}>
                  <Stack gap="md" align="flex-start">
                    {/* Icon sits inline with the headline at 22px, not in a chip above it:
                        Tabler is drawn for this scale, and it anchors the card without the
                        centered pastel disc this section used to use. */}
                    <Group gap="xs" align="flex-start" wrap="nowrap">
                      <IconSearch
                        size={22}
                        color="var(--mantine-color-orange-6)"
                        style={{ flexShrink: 0, marginTop: 5 }}
                      />
                      <Title order={3} c="dark">
                        Finding recipes is hard
                      </Title>
                    </Group>
                    <Text c="dimmed">
                      When you're managing multiple dietary restrictions, you can't just
                      search for "easy weeknight dinner." Every recipe needs to work for
                      all of your needs at once, and most don't.
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Paper p="xl" radius="lg" h="100%" style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid var(--mantine-color-orange-2)'
                }}>
                  <Stack gap="md" align="flex-start">
                    {/* Icon sits inline with the headline at 22px, not in a chip above it:
                        Tabler is drawn for this scale, and it anchors the card without the
                        centered pastel disc this section used to use. */}
                    <Group gap="xs" align="flex-start" wrap="nowrap">
                      <IconMoodSad
                        size={22}
                        color="var(--mantine-color-orange-6)"
                        style={{ flexShrink: 0, marginTop: 5 }}
                      />
                      <Title order={3} c="dark">
                        Plans that miss the mark
                      </Title>
                    </Group>
                    <Text c="dimmed">
                      Generic meal plans don't work when you're low-sodium <em>and</em> dairy-free.
                      Most tools handle one restriction at a time. Real life isn't that simple.
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 4 }}>
                <Paper p="xl" radius="lg" h="100%" style={{
                  background: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid var(--mantine-color-orange-2)'
                }}>
                  <Stack gap="md" align="flex-start">
                    {/* Icon sits inline with the headline at 22px, not in a chip above it:
                        Tabler is drawn for this scale, and it anchors the card without the
                        centered pastel disc this section used to use. */}
                    <Group gap="xs" align="flex-start" wrap="nowrap">
                      <IconPackage
                        size={22}
                        color="var(--mantine-color-orange-6)"
                        style={{ flexShrink: 0, marginTop: 5 }}
                      />
                      <Title order={3} c="dark">
                        Meal kits can't keep up
                      </Title>
                    </Group>
                    <Text c="dimmed">
                      $10-15 per serving, mountains of packaging, and they still can't
                      handle your specific dietary needs. You're paying more for less
                      flexibility.
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* ──────────────────────────────────────────────
          SECTION 3: THE SOLUTION
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Grid align="center" gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="lg" px={{ base: 'md', xl: 0 }}>
              <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
                One plan that handles all of it
              </Title>
              <Text size="lg" c="dimmed" lh={1.6}>
                Meals You Love creates personalized weekly meal plans built around
                your specific dietary restrictions. Not just one at a time, but all of them together.
              </Text>

              <List
                spacing="sm"
                size="md"
                icon={
                  <IconCheck size={16} stroke={2.4} color="var(--mantine-color-orange-6)" />
                }
                styles={{
                  itemWrapper: {
                    alignItems: 'flex-start'
                  },
                }}
              >
                <List.Item><Text c="gray.8">Gluten-Free</Text></List.Item>
                <List.Item><Text c="gray.8">Dairy-Free</Text></List.Item>
                <List.Item><Text c="gray.8">Low-Sodium</Text></List.Item>
                <List.Item><Text c="gray.8">Low-Sugar & Diabetic-Friendly</Text></List.Item>
                <List.Item><Text c="gray.8">Low-Residue & Low-Fiber</Text></List.Item>
                <List.Item><Text c="gray.8">Nut-Free, Soy-Free & Egg-Free</Text></List.Item>
                <List.Item><Text c="gray.8">Anti-Inflammatory</Text></List.Item>
                <List.Item><Text c="gray.8">High-Fiber & Heart-Healthy</Text></List.Item>
              </List>

              <Text size="sm" c="dimmed" fs="italic">
                17+ dietary options, and you can combine as many as you need.
              </Text>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Center>
              <Image
                src="/images/meal_plan.png"
                alt="Personalized weekly meal plan"
                maw="min(500px, 100%)"
                radius="lg"
                style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)' }}
              />
            </Center>
          </Grid.Col>
        </Grid>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 4: DIFFERENTIATOR
      ────────────────────────────────────────────── */}
      <Box
        py={{ base: 40, md: 80 }}
        style={{
          background: 'linear-gradient(45deg, var(--mantine-color-gray-0) 0%, rgba(255, 248, 240, 1) 100%)'
        }}
      >
        <Container size="lg">
          <Stack gap="xl">
            <Center>
              <Stack gap="sm" align="center" maw={650}>
                <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                  Not a meal kit. Not another recipe app.
                </Title>
                <Text size="lg" ta="center" c="dimmed">
                  The structure of a meal kit, the freedom to shop your way, at a fraction of the price.
                </Text>
              </Stack>
            </Center>

            <Grid align="stretch" justify="center" gutter="xl">
              {[
                {
                  icon: IconCalendarWeek,
                  title: 'Structured weekly plans',
                  description: 'Dinner for the whole week, planned and organized, with every recipe tailored to your dietary needs.'
                },
                {
                  icon: IconListCheck,
                  title: 'Complete shopping lists',
                  description: 'Consolidated ingredient lists with nothing missing. No more guessing quantities or forgetting items.'
                },
                {
                  icon: IconBrain,
                  title: 'Every restriction, covered',
                  description: 'Gluten-free and low-sodium and dairy-free? Every recipe in your plan respects all of your dietary needs at once.'
                },
                {
                  icon: IconCoin,
                  title: 'Know what dinner costs',
                  description: 'See the price per serving for every meal and how it compares to takeout. Plan around your budget, not just your calendar.'
                },
                {
                  icon: IconShoppingCart,
                  title: 'Your grocery store, your way',
                  description: 'Send to Kroger, order through Instacart, or download a list and shop in-store. Your call.'
                }
              ].map((feature, index) => (
                <Grid.Col key={index} span={{ base: 12, sm: 6, lg: 4 }}>
                  <Paper p="xl" radius="lg" h="100%" ta="center" style={{
                    border: '1px solid var(--mantine-color-gray-2)'
                  }}>
                    <Stack gap="md" align="center">
                      <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                        <feature.icon size={30} />
                      </ThemeIcon>
                      <Title order={4} c="dark">{feature.title}</Title>
                      <Text size="sm" c="dimmed" lh={1.5}>{feature.description}</Text>
                    </Stack>
                  </Paper>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* ──────────────────────────────────────────────
          SECTION 5: MEAL KIT COMPARISON
      ────────────────────────────────────────────── */}
      <Box
        style={{
          background: `
            radial-gradient(56.73% 109.64% at 31.11% 87.57%, rgba(255, 146, 43, 0.08) 0%, rgba(255, 255, 255, 0) 90%),
            radial-gradient(53.27% 82.76% at 98.19% 100%, rgba(255, 99, 71, 0.06) 0%, rgba(255, 255, 255, 0) 90%),
            radial-gradient(43.01% 85.58% at 55.35% 107.63%, rgba(255, 193, 7, 0.07) 0%, rgba(255, 255, 255, 0) 90%)
          `
        }}
      >
      <Container size="lg" py={{ base: 40, md: 80 }}>
          <Stack gap="xl">
            <Center>
              <Stack gap="sm" align="center" maw={700}>
                <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                  More control. Less waste.
                </Title>
                <Text size="lg" ta="center" c="dimmed">
                  Meal kits choose for you. We let you choose for yourself.
                </Text>
              </Stack>
            </Center>

            <Box style={{ position: 'relative' }}>
              <Grid gutter={{ base: 'md', sm: 'xl' }} align="center">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Box p="xl" style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    height: '100%',
                    border: '1px solid var(--mantine-color-orange-3)',
                    background: 'var(--mantine-color-orange-0)'
                  }}>
                    <Stack gap="lg">
                      <Group justify="center">
                        <Image
                          src="/icon.png"
                          alt="Meals You Love"
                          w={40}
                          h={40}
                          style={{ borderRadius: 'var(--mantine-radius-xl)' }}
                        />
                        <Title order={3} c="orange.8">
                          Meals You Love
                        </Title>
                      </Group>
                      <List
                        spacing="sm"
                        size="md"
                        icon={
                          <ThemeIcon color="orange" size={24} radius="xl">
                            <IconCheck size={16} />
                          </ThemeIcon>
                        }
                        styles={{
                          itemWrapper: {
                            alignItems: 'flex-start'
                          },
                        }}
                      >
                        <List.Item><Text c="gray.8"><Text span fw={600}>Handles complex diets:</Text> gluten-free and low-sodium and dairy-free, all at once</Text></List.Item>
                        <List.Item><Text c="gray.8"><Text span fw={600}>Your grocery store:</Text> broader selection, lower prices, less packaging</Text></List.Item>
                        <List.Item><Text c="gray.8"><Text span fw={600}>Convenient shopping:</Text> Kroger pickup, Instacart delivery, or shop on your own</Text></List.Item>
                        <List.Item><Text c="gray.8"><Text span fw={600}>$9.99/month</Text> for personalized weekly dinner plans</Text></List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Grid.Col>

                {/* VS Element - Mobile */}
                <Grid.Col span={12} hiddenFrom="sm" style={{ marginTop: '-20px', marginBottom: '-20px', zIndex: 10 }}>
                  <Center>
                    <Box
                      style={{
                        background: 'white',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid var(--mantine-color-gray-4)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Text fw={500} c="gray.6" size="sm">vs</Text>
                    </Box>
                  </Center>
                </Grid.Col>

                {/* VS Element - Desktop */}
                <Box
                  visibleFrom="sm"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'white',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid var(--mantine-color-gray-4)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    zIndex: 10
                  }}
                >
                  <Text fw={500} c="gray.6" size="sm">vs</Text>
                </Box>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Box p="xl" style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    height: '100%',
                    border: '1px solid var(--mantine-color-gray-2)',
                    background: 'var(--mantine-color-gray-0)'
                  }}>
                    <Stack gap="lg">
                      <Group justify="center">
                        <ThemeIcon size={40} radius="xl" color="gray" variant="light">
                          <IconPackage size={20} />
                        </ThemeIcon>
                        <Title order={3} c="gray.7">
                          Meal kit services
                        </Title>
                      </Group>
                      <List
                        spacing="sm"
                        size="md"
                        icon={
                          /* A plain muted disc, no glyph: the ✕ read as clutter at this size,
                             and the column already says "not this" through its
                             gray treatment and the ✓ column opposite it. */
                          <ThemeIcon color="gray" size={24} radius="xl" />
                        }
                        styles={{
                          itemWrapper: {
                            alignItems: 'flex-start'
                          },
                        }}
                      >
                        <List.Item><Text c="gray.6"><Text span fw={600}>Limited customization:</Text> a handful of recipes, take it or leave it</Text></List.Item>
                        <List.Item><Text c="gray.6"><Text span fw={600}>Locked supply chain:</Text> no store choice, no flexibility, no way to add extras</Text></List.Item>
                        <List.Item><Text c="gray.6"><Text span fw={600}>Mountains of packaging:</Text> ice packs, boxes, individual wrapping</Text></List.Item>
                        <List.Item><Text c="gray.6"><Text span fw={600}>$250-500+/month:</Text> costs add up fast for families</Text></List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Grid.Col>
              </Grid>
            </Box>
          </Stack>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 6: SHOP YOUR WAY
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
          <Grid align="center" gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 2, md: 1 }}>
              <Stack gap={60} align="center">
                <Image
                  src="/images/kroger_integration_logo.svg"
                  alt="Kroger integration"
                  maw={200}
                />
                <Image
                  src="/images/instacart_logo_large.png"
                  alt="Instacart integration"
                  maw={200}
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 1, md: 2 }}>
              <Stack gap="lg" px={{ base: 'md', xl: 0 }}>
                <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
                  Shop wherever you want
                </Title>
                <Text size="lg" c="dimmed" lh={1.6}>
                  Your plan works with your grocery store, not the other way around.
                  No subscription boxes, no forced brands, no markup on ingredients.
                </Text>
                <List
                  spacing="sm"
                  size="md"
                  icon={
                    <IconCheck size={16} stroke={2.4} color="var(--mantine-color-green-7)" />
                  }
                  styles={{
                    itemWrapper: {
                      alignItems: 'flex-start'
                    },
                  }}
                >
                  <List.Item><Text c="gray.8">Add everything to your Kroger cart for pickup or delivery</Text></List.Item>
                  <List.Item><Text c="gray.8">Send to Instacart for same-day delivery</Text></List.Item>
                  <List.Item><Text c="gray.8">Download a categorized shopping list and shop anywhere</Text></List.Item>
                  <List.Item><Text c="gray.8">Choose your own brands, portions, and budget</Text></List.Item>
                </List>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>

      {/* ──────────────────────────────────────────────
          SOCIAL PROOF
      ────────────────────────────────────────────── */}
      <SocialProof background="white" withDivider />

      {/* ──────────────────────────────────────────────
          SECTION 7: PRICING
      ────────────────────────────────────────────── */}
        <Container size="lg" py={{ base: 40, md: 80 }}>
        <Stack gap="xl">
          <Center>
            <Stack gap="sm" align="center" maw={600}>
              <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                Pricing
              </Title>
              <Text size="lg" ta="center" c="dimmed">
                Start with a full month free - no credit card required
              </Text>
            </Stack>
          </Center>

          <Center>
            <Paper p={{ base: 40, md: 60 }} radius="xl" maw={500} w="100%" style={{
              border: '2px solid var(--mantine-color-orange-4)',
              position: 'relative'
            }}>
              <Box
                style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--mantine-color-orange-filled)',
                  color: 'white',
                  padding: '4px 16px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                1 MONTH FREE
              </Box>

              <Stack gap="lg" align="center" ta="center">
                <Stack gap="xs" align="center">
                  <Text size="3rem" fw={700} c="dark" style={{ lineHeight: 1 }}>
                    $9.99
                  </Text>
                  <Text size="md" c="dimmed">
                    per month after trial
                  </Text>
                </Stack>

                <Stack gap="sm" align="flex-start" style={{ textAlign: 'left' }}>
                  <Flex gap="md" align="flex-start">
                    <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                    <Text size="md" c="gray.8">Personalized weekly meal plans</Text>
                  </Flex>
                  <Flex gap="md" align="flex-start">
                    <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                    <Text size="md" c="gray.8">Intelligent recipe suggestions</Text>
                  </Flex>
                  <Flex gap="md" align="flex-start">
                    <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                    <Text size="md" c="gray.8">Grocery shopping integration</Text>
                  </Flex>
                  <Flex gap="md" align="flex-start">
                    <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                    <Text size="md" c="gray.8">Recipe collection management</Text>
                  </Flex>
                  <Flex gap="md" align="flex-start">
                    <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                    <Text size="md" c="gray.8">Pantry tracking & meal prepping</Text>
                  </Flex>
                </Stack>

                <AuthButton size="lg">
                  Start free trial
                </AuthButton>

                <Text size="sm" c="dimmed">
                  No credit card required • Cancel anytime
                </Text>
              </Stack>
            </Paper>
          </Center>
          <Center>
            <Text size="xs" c="dimmed" maw={500} ta="center" lh={1.5}>
              Meals You Love supports dietary preferences and restrictions but does not provide
              medical advice. Always consult your healthcare provider for medical dietary guidance.
            </Text>
          </Center>
        </Stack>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 8: FINAL CTA
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Center>
          <Stack gap="xl" align="center" ta="center" maw={900}>
            <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
              Ready to build a plan that works for you?
            </Title>
            <Text size="lg" c="dimmed" maw={600}>
              Personalized weekly meal plans built around your health, your grocery store,
              and your budget. Simple. Flexible. Built around you.
            </Text>
            <AuthButton size="xl">
              Start your free trial
            </AuthButton>
            <Group gap="xs" justify="center">
              <Text size="sm" c="dimmed">
                1-month free trial • No credit card required
              </Text>
            </Group>
          </Stack>
        </Center>
      </Container>
      </Box>
    </Box>
  );
};
